import React from 'react';
import { useTranslation } from 'react-i18next';
import { BsCalendar2, BsCashStack } from 'react-icons/bs';
import { PiHandHeart } from 'react-icons/pi';
import { useNavigate, useLocation } from 'react-router';

import { useBookingStore } from '@/store';

interface Step {
  id: number;
  titleKey: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
}

const Stepper: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isStep1Complete, isStep2Complete } = useBookingStore();

  const steps: Step[] = [
    {
      id: 1,
      titleKey: 'navigation.step1',
      path: '/booking/select-date-time',
      icon: BsCalendar2,
    },
    {
      id: 2,
      titleKey: 'navigation.step2',
      path: '/booking/select-treatment',
      icon: PiHandHeart,
    },
    {
      id: 3,
      titleKey: 'navigation.step3',
      path: '/booking/summary',
      icon: BsCashStack,
    },
  ];

  const getCurrentStep = () => {
    const step = steps.find((s) => location.pathname.startsWith(s.path));
    return step ? step.id : 1;
  };

  const currentStep = getCurrentStep();

  const isStepAccessible = (stepId: number) => {
    if (stepId === 1) return true; // First step is always accessible
    if (stepId === 2) return isStep1Complete(); // Second step needs first step complete
    if (stepId === 3) return isStep1Complete() && isStep2Complete(); // Third step needs first two complete
    return false;
  };

  const handleStepClick = (step: Step) => {
    // Only allow navigation to accessible steps that are not future steps
    if (step.id <= currentStep && isStepAccessible(step.id)) {
      navigate(step.path);
    }
  };

  const getStepStyles = (stepId: number) => {
    const isActive = stepId === currentStep;
    const isCompleted = stepId < currentStep && isStepAccessible(stepId);
    const isAccessible = isStepAccessible(stepId);

    if (isActive) {
      return {
        circle: 'bg-secondary text-white shadow-md',
        text: 'text-primary-text font-semibold',
        cursor: 'cursor-pointer',
        connector: stepId < 3 ? 'bg-secondary' : '',
      };
    } else if (isCompleted) {
      return {
        circle: 'bg-secondary text-white shadow-sm',
        text: 'text-label font-medium',
        cursor: 'cursor-pointer hover:opacity-80',
        connector: stepId < currentStep ? 'bg-secondary' : 'bg-border-disabled',
      };
    } else if (isAccessible) {
      return {
        circle: 'bg-white border-secondary border-1 text-secondary shadow-sm',
        text: 'text-label font-medium',
        cursor: 'cursor-pointer',
        connector: 'bg-border-disabled',
      };
    } else {
      return {
        circle: 'bg-white border-secondary border-1 text-secondary',
        text: 'text-label font-normal',
        cursor: 'cursor-not-allowed',
        connector: 'bg-border-disabled',
      };
    }
  };

  return (
    <div className="mx-auto w-full max-w-md px-6 pt-8 pl-3.5">
      <div className="relative flex items-center justify-between">
        {/* Connector lines */}
        <div className="absolute top-5 right-0 left-0 flex justify-between px-5">
          {[1, 2].map((connectorIndex) => {
            const styles = getStepStyles(connectorIndex);
            return (
              <div
                key={connectorIndex}
                className={`h-[1px] w-[calc(50%-10px)] transition-colors duration-300 ${styles.connector}`}
              />
            );
          })}
        </div>

        {steps.map((step) => {
          const styles = getStepStyles(step.id);
          const IconComponent = step.icon;

          return (
            <div
              key={step.id}
              className="relative z-10 flex flex-col items-center gap-2"
              onClick={() => handleStepClick(step)}
            >
              <div
                className={`flex size-10 items-center justify-center rounded-full transition-all duration-300 ${styles.circle} ${styles.cursor} `}
              >
                <IconComponent className="size-5" />
              </div>
              <div
                className={`text-center text-sm leading-5 whitespace-nowrap transition-all duration-300 ${styles.text} `}
              >
                {t(step.titleKey)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Stepper;
